import React, { useState, useMemo, useCallback, memo } from "react";
import { Calendar, Edit2, ChevronDown, ChevronUp } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { addTodaysTourReportApi } from "@services/MarketWorking/addTodaysTourReportApi";
import { getTodaysDate } from "@components/Common/Function";
import { Loader } from "../../shared/components";
import CommonSnackbar from "@components/Common/CommonSnackbar";

// Lazy load NewFeedback - it's a large component
const NewFeedback = React.lazy(() => import("./NewFeedback"));

/**
 * TotalFeedback Component
 * 
 * Displays today's observations/feedback with inline edit functionality.
 * Users can edit existing observations and submit changes.
 * Includes expandable view (first 3 items, then "View all").
 * 
 * Performance optimized with React.memo and lazy loading.
 */
const TotalFeedback = () => {
  const dispatch = useDispatch();
  const initialState = 3;
  const [noOfElement, setNoOfElement] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [onClick, setOnClick] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [observation, setObservation] = useState("");
  const [id, setId] = useState();

  // Memoize feedback selector to prevent unnecessary re-renders
  const feedBack = useSelector(
    (state) => state?.getFeedback?.getFeedbackDetails?.Feedback_Today,
    (prev, next) => prev?.length === next?.length // Custom equality check
  );

  const feedbackLength = useMemo(() => feedBack?.length || 0, [feedBack]);

  // Memoize sliced feedback
  const slicedFeedback = useMemo(() => {
    return feedBack?.slice(0, noOfElement) || [];
  }, [feedBack, noOfElement]);

  const loadMore = useCallback((e) => {
    e.preventDefault();
    setNoOfElement((prev) => (prev === initialState ? feedbackLength : initialState));
  }, [feedbackLength]);

  const handleOnClose = useCallback(() => {
    setSuccessOpen(false);
  }, []);

  const onSubmit = useCallback(async (recordId) => {
    if (observation.trim()) {
      setLoading(true);
      const response = await dispatch(addTodaysTourReportApi({
        RecordId: recordId,
        Observation: observation,
      }));
      setLoading(false);
      if (response?.success) {
        setSuccessOpen(true);
        setId();
      }
      setObservation("");
    }
  }, [observation, dispatch]);

  const handleEdit = useCallback((itemId) => {
    setId(itemId);
    setOnClick(true);
  }, []);

  const handleSubmitClick = useCallback((itemId) => {
    onSubmit(itemId);
    setOnClick(false);
  }, [onSubmit]);

  return (
    <>
      <CommonSnackbar
        open={successOpen}
        autoHideDuration={3000}
        onClose={handleOnClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        severity="success"
        message="Observation edited successfully"
      />

      <div className="px-4 py-2.5">
        <h6 className="text-[#3901ff] font-semibold my-0 py-2.5">Total Observations</h6>
        <Loader loading={loading} />

        <div className="bg-white shadow-[1.25px_1.25px_5.34px_rgba(20,20,20,0.07)] rounded-[2.77px] pt-2 w-full">
          <div className="flex justify-between items-center mx-0 px-3">
            <h6 className="flex justify-start text-[#808080] font-medium text-[13.46px]">
              Today
            </h6>
            <h6 className="flex justify-end font-medium text-[13.46px] text-[#f27042]">
              {feedBack?.length || 0} Observations
            </h6>
          </div>

          <div className="flex gap-[0.7rem] font-maven font-medium text-[9.92px] text-[#808080] pr-0 mx-0 pl-3 mb-2">
            <Calendar size={14} />
            <p className="py-0.5 mb-0">{getTodaysDate()}</p>
          </div>

          <div className="max-h-[190px] overflow-auto">
            {slicedFeedback?.map((item) => (
              <div
                key={item?.Id}
                className="border-l-[3px] border-[#969696] bg-[rgba(150,150,150,0.13)] mb-2 min-h-auto max-h-8 whitespace-nowrap mx-0 flex items-center"
              >
                <div className="flex w-full p-0">
                  <textarea
                    onChange={(e) => setObservation(e.target.value)}
                    disabled={item?.Id !== id}
                    className={`w-full flex items-center justify-between px-[0.625rem] py-[0.2rem] font-maven font-medium text-[0.6rem] text-[#2b2b2b] resize-none h-[25px] ${
                      onClick && item.Id === id
                        ? "border-[#2cc26a]"
                        : "border-[#F5F5F5]"
                    }`}
                    defaultValue={item.Observations__c}
                  />

                  <div className="flex items-center pl-[5px] pr-[10px] py-0">
                    {onClick && item.Id === id ? (
                      <button
                        className="bg-[#2cc26a] rounded-[0.3rem] border-[#2cc26a] text-[#f9f9f9] font-roboto font-normal text-[0.606rem] leading-[0.8rem] whitespace-nowrap px-2 py-1 hover:bg-[#28b05f] transition-colors"
                        onClick={() => handleSubmitClick(item?.Id)}
                      >
                        Submit
                      </button>
                    ) : (
                      <Edit2
                        size={14}
                        className="cursor-pointer hover:text-[#3901ff] transition-colors"
                        onClick={() => handleEdit(item?.Id)}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {feedbackLength > 3 && (
            <div className="flex justify-end h-[1.6rem] pt-0.5 mx-0 px-3">
              <button
                className="bg-white border-none pl-0 flex text-center items-center hover:opacity-80 transition-opacity"
                onClick={loadMore}
              >
                {slicedFeedback.length > 0 && (
                  <div className="flex items-center">
                    <p className="font-roboto font-medium text-[8.92254px] text-[#2cc26a] whitespace-nowrap mb-0">
                      {noOfElement === initialState ? "View all" : "See less"}
                    </p>
                    {noOfElement === initialState ? (
                      <ChevronDown className="text-[#2cc26a] h-[13px] w-[10px] ml-1.5 stroke-1" />
                    ) : (
                      <ChevronUp className="text-[#2cc26a] h-[13px] w-[10px] ml-1.5 stroke-1" />
                    )}
                  </div>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* New Feedback Form - Lazy loaded for better performance */}
      <React.Suspense fallback={<Loader loading={true} />}>
        <NewFeedback />
      </React.Suspense>
    </>
  );
};

// Memoize component to prevent unnecessary re-renders
export default memo(TotalFeedback);
